// ABOUT INBA (/about) — OVERVIEW · STRENGTHS · FRAMEWORK · HOW WE WORK · LOCAL NETWORK
// 디자인 시안 HTML을 그대로 옮긴 화면입니다. 문구·사진·구성은 시안과 같게 유지해 주세요.
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePageMotion } from "../motion/usePageMotion";
import { useHoverIntent } from "../motion/useHoverIntent";

// HOW WE WORK 4단계
const STAGES = [
  {
    tab: "01 / STRATEGY",
    name: "PLAN",
    ko: "기획·기준 설정",
    desc: "사업의 목적과 수행 범위를 명확히 정의하고, 한국과 태국의 역할·일정·품질 기준을 함께 정합니다.",
    context: "사업계획 수립 · 발주기관 협의 · 수행 범위·역할 조정",
  },
  {
    tab: "02 / PARTNERS",
    name: "CONNECT",
    ko: "현지 관계자 연결",
    desc: "프로젝트 목적에 맞는 현지 바이어, 유통채널, 크리에이터, 협력업체 및 운영 인력을 연결하고 실제 협력 조건을 구체화합니다.",
    context: "바이어·협력사 발굴 · 상담·협력 조건 조율 · 실행 기반 확보",
  },
  {
    tab: "03 / OPERATION",
    name: "EXECUTE",
    ko: "사업·현장 실행",
    desc: "합의한 계획에 맞춰 수입·유통, 디지털 마케팅, 전시·팝업·수출상담 등 현지 사업을 수행하고 진행 상황을 관리합니다.",
    context: "현장 진행 · 협력사·인력 관리 · 일정·품질 점검",
  },
  {
    tab: "04 / RESULTS",
    name: "REVIEW",
    ko: "성과·결과 정리",
    desc: "운영 실적과 결과 자료를 정리하고, 고객 및 발주기관에 보고하여 후속 실행과 개선 방향을 연결합니다.",
    context: "운영 데이터 수집 · 결과보고 · 후속 사업 검토",
  },
];
const METRIC_COUNT = 3;
const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const reflow = (el) => el.getBoundingClientRect();

export default function About() {
  const mainRef = useRef(null);
  const [activeSection, setActiveSection] = useState("about01");
  const [metric, setMetric] = useState(0);
  const metricRefs = useRef([]);
  const countTimers = useRef(new Map());
  const [wfTab, setWfTab] = useState(0);
  const [wfShown, setWfShown] = useState(0);
  const [wfSoft, setWfSoft] = useState(false);
  const wfCurrent = useRef(0);
  const wfTimer = useRef(null);
  const wfTabRefs = useRef([]);

  // .js .reveal 규칙(등장 효과 전 숨김)이 첫 화면부터 적용되도록 그리기 전에 붙인다
  useLayoutEffect(() => {
    document.body.classList.add("js");
    return () => document.body.classList.remove("js");
  }, []);

  // 등장 효과
  useEffect(() => {
    const reveals = [...mainRef.current.querySelectorAll(".reveal")];
    if (reducedMotion() || !("IntersectionObserver" in window)) {
      reveals.forEach((el) => el.classList.add("in"));
      return undefined;
    }
    const ob = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            ob.unobserve(e.target);
          }
        }
      },
      { threshold: 0.09 }
    );
    reveals.forEach((el) => ob.observe(el));
    return () => ob.disconnect();
  }, []);

  // 보조 메뉴: 스크롤 위치에 따라 현재 구역 표시
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;
    const sections = [...mainRef.current.querySelectorAll("section[id]")];
    const so = new IntersectionObserver(
      (entries) => {
        const a = entries
          .filter((e) => e.isIntersecting)
          .sort((x, y) => x.boundingClientRect.top - y.boundingClientRect.top)[0];
        if (a) setActiveSection(a.target.id);
      },
      { rootMargin: "-130px 0px -50% 0px" }
    );
    sections.forEach((s) => so.observe(s));
    return () => so.disconnect();
  }, []);
  const navLink = (id) => ({
    className: activeSection === id ? "active" : undefined,
    "aria-current": activeSection === id ? "location" : undefined,
    onClick: () => setActiveSection(id),
  });

  // CORE STRENGTHS: 숫자가 다시 올라가는 효과 (게이지 길이는 장식, 수치 비교 아님)
  const runCount = useCallback((i) => {
    const card = metricRefs.current[i];
    if (!card) return;
    const el = card.querySelector(".metric-number");
    const bar = card.querySelector(".metric-fill");
    if (!el || !bar) return;
    const target = Number(el.dataset.target);
    const prefix = el.dataset.prefix || "";
    const duration = 1050;
    const timers = countTimers.current;
    const previous = timers.get(i);
    if (previous) cancelAnimationFrame(previous);
    bar.style.transition = "none";
    bar.style.width = "0%";
    el.textContent = prefix + "0";
    if (reducedMotion()) {
      el.textContent = prefix + target.toLocaleString("ko-KR");
      bar.style.width = bar.dataset.fill + "%";
      return;
    }
    reflow(bar);
    bar.style.transition = "width 1.05s cubic-bezier(.2,.7,.25,1)";
    requestAnimationFrame(() => {
      bar.style.width = bar.dataset.fill + "%";
    });
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = prefix + Math.round(target * eased).toLocaleString("ko-KR");
      if (p < 1) timers.set(i, requestAnimationFrame(tick));
      else timers.delete(i);
    };
    timers.set(i, requestAnimationFrame(tick));
  }, []);

  // 카드를 바꿔도 숫자는 다시 0부터 올라가지 않는다(10번). 마우스는 잠깐 머물 때만 바뀐다.
  const metricIntent = useHoverIntent();
  const chooseMetric = (i) => {
    metricIntent.leave();
    setMetric(i);
  };

  // 처음 화면에 들어오면 3개 숫자가 차례로 올라감(한 번만)
  useEffect(() => {
    const metricsEl = mainRef.current.querySelector("#about02 .metrics");
    const timeouts = [];
    const timers = countTimers.current;
    let mo = null;
    if ("IntersectionObserver" in window) {
      let seen = false;
      mo = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting) && !seen) {
            seen = true;
            for (let i = 0; i < METRIC_COUNT; i++) timeouts.push(setTimeout(() => runCount(i), i * 95));
            mo.disconnect();
          }
        },
        { threshold: 0.18 }
      );
      mo.observe(metricsEl);
    } else {
      for (let i = 0; i < METRIC_COUNT; i++) runCount(i);
    }
    return () => {
      if (mo) mo.disconnect();
      timeouts.forEach(clearTimeout);
      timers.forEach((id) => cancelAnimationFrame(id));
      timers.clear();
    };
  }, [runCount]);

  const metricTab = (i) => ({
    ref: (el) => {
      metricRefs.current[i] = el;
    },
    "aria-selected": metric === i ? "true" : "false",
    className: "metric-card" + (metric === i ? " selected" : ""),
    onMouseEnter: () => metricIntent.enter(() => setMetric(i)),
    onMouseLeave: metricIntent.leave,
    onFocus: () => chooseMetric(i),
    onClick: () => chooseMetric(i),
    onKeyDown: (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        const n = (i + (e.key === "ArrowRight" ? 1 : METRIC_COUNT - 1)) % METRIC_COUNT;
        if (metricRefs.current[n]) metricRefs.current[n].focus();
      }
    },
  });
  const metricPanel = (i) => ({
    "aria-hidden": metric === i ? "false" : "true",
    className: "experience-panel" + (metric === i ? " selected" : ""),
    inert: metric !== i,
  });

  // HOW WE WORK: 같은 자리에서 0.17초 부드럽게 내용 교체
  const workflow = useCallback((i) => {
    if (i === wfCurrent.current) return;
    wfCurrent.current = i;
    setWfTab(i);
    setWfSoft(true);
    clearTimeout(wfTimer.current);
    wfTimer.current = setTimeout(
      () => {
        setWfShown(i);
        setWfSoft(false);
      },
      reducedMotion() ? 0 : 170
    );
  }, []);
  useEffect(() => () => clearTimeout(wfTimer.current), []);
  const wfIntent = useHoverIntent(); // 마우스는 잠깐 머물 때만 단계가 바뀐다(11번)
  const stage = STAGES[wfShown];

  // 첫 화면 제목과 구역 제목은 단어별로 올라온다
  usePageMotion(mainRef, { split: [".intro-message h1", ".section-h2"] });

  return (
    <>
      <div className="section-nav">
        <div className="wrap">
          <div className="where"><b>02</b>{" ABOUT INBA"}</div>
          <nav aria-label="ABOUT 페이지 내부 이동">
            <a {...navLink("about01")} href="#about01">01 / OVERVIEW</a>
            <a {...navLink("about02")} href="#about02">02 / STRENGTHS</a>
            <a {...navLink("about03")} href="#about03">03 / FRAMEWORK</a>
          </nav>
        </div>
      </div>
      <main ref={mainRef}>
        <section className="intro" id="about01">
          <div className="wrap">
            <div className="intro-stage">
              <div className="intro-main">
                <div className="intro-message">
                  <div className="micro">ABOUT INBA / WHO WE ARE</div>
                  <h1>
                    시장에 진입하는 것과,
                    <br />
                    {"시장에서 "}
                    <span className="blue">비즈니스를</span>
                    <br />
                    <span className="blue">만드는 것은</span>
                    {" 다릅니다."}
                  </h1>
                  <p className="intro-lead">INBA Thailand는<br />그 차이를 현장에서 메웁니다.</p>
                  <div className="intro-copy">
                    <p>2017년 태국에 설립된 INBA(Thailand)는 한국 기업과 공공기관의 태국 시장 진출 및 현지 사업을 지원하는 실행형 비즈니스 기업입니다.</p>
                    <p>시장·제품 검토부터 수입·인허가, 유통채널 연결, 디지털 마케팅, 전시·수출상담회 운영까지. 한국의 기획·협업 역량과 태국 현지의 조직·네트워크를 바탕으로 사업의 각 단계를 연결하고 실행합니다.</p>
                  </div>
                </div>
                <div aria-label="INBA Thailand의 다양한 전시·유통·상담·행사 수행 현장 사진 모음" className="intro-photo story-mosaic">
                  <figure className="story-tile tile-primary">
                    <img style={{ objectPosition: "50% 55%" }} alt="대구국제뷰티엑스포 태국관에서 태국 진출 상담을 진행하는 모습" src="/assets/img/photos/pf-05-g2.jpg" />
                    <span className="tile-caption">ON THE GROUND / THAILAND</span>
                  </figure>
                  <figure className="story-tile tile-exhibition">
                    <img style={{ objectPosition: "62% 50%" }} alt="대구국제뷰티엑스포 태국관 부스 전경" src="/assets/img/photos/about-mosaic-exhibition.jpg" />
                  </figure>
                  <figure className="story-tile tile-buyer">
                    <img alt="한국 브랜드의 현지 비즈니스 상담" src="/assets/img/pages/about-mosaic-paradise-staff.jpg" />
                  </figure>
                  <figure className="story-tile tile-event">
                    <img style={{ objectPosition: "50% 40%" }} alt="한국유학박람회 aT 체험존 운영 모습" src="/assets/img/photos/pf-03-g6.jpg" />
                  </figure>
                  <figure className="story-tile tile-team">
                    <img style={{ objectPosition: "50% 35%" }} alt="INBA Thailand 방콕 사무실 팀" src="/assets/img/photos/about-mosaic-team.jpg" />
                  </figure>
                  <figure className="story-tile tile-consulting">
                    <img alt="현지 제품 상담·홍보 현장" src="/assets/img/pages/about-mosaic-paradise-promo.jpg" />
                  </figure>
                </div>
              </div>
              <div aria-label="회사 정보" className="intro-ribbon">
                <div className="fact"><strong>2017</strong><small>ESTABLISHED</small></div>
                <div className="fact"><strong>BANGKOK</strong><small>THAILAND BASE</small></div>
                <div className="fact"><strong>KR × TH</strong><small>OPERATING LINK</small></div>
              </div>
            </div>
          </div>
        </section>
        <section className="capabilities" id="about02">
          <div className="wrap">
            <div className="section-head reveal">
              <div>
                <div className="micro">ABOUT-02 / CORE STRENGTHS</div>
                <h2 className="section-h2">경험으로 증명하는<br />INBA의 실행 역량.</h2>
              </div>
              <div className="right">현지 유통, 디지털 커머스, 행사 운영.<br /><strong>연결에서 끝나지 않는 현장의 경험</strong>을 소개합니다.</div>
            </div>
            <div className="score-intro"><span>EXPERIENCE IN NUMBERS</span><small>실제 수행 경험을 대표하는 세 가지 지표</small></div>
            <div aria-label="경쟁력별 수행 경험" className="metrics" role="tablist">
              <button aria-controls="experience-0" {...metricTab(0)} data-metric="0" id="metric-tab-0" role="tab" type="button">
                <span className="metric-label">01 / DISTRIBUTION</span>
                <span className="metric-number-row">
                  <strong className="metric-number" data-prefix="" data-target="42">42</strong>
                  <span className="metric-unit">STORES</span>
                </span>
                <span className="metric-sub">태국 백화점 유통 점포</span>
                <span aria-hidden="true" className="metric-track"><span className="metric-fill" data-fill="76" /></span>
              </button>
              <button aria-controls="experience-1" {...metricTab(1)} data-metric="1" id="metric-tab-1" role="tab" type="button">
                <span className="metric-label">02 / DIGITAL COMMERCE</span>
                <span className="metric-number-row">
                  <strong className="metric-number" data-prefix="" data-target="31">31</strong>
                  <span className="metric-unit">CREATORS</span>
                </span>
                <span className="metric-sub">2025 인플루언서 참여</span>
                <span aria-hidden="true" className="metric-track"><span className="metric-fill" data-fill="63" /></span>
              </button>
              <button aria-controls="experience-2" {...metricTab(2)} data-metric="2" id="metric-tab-2" role="tab" type="button">
                <span className="metric-label">03 / ON-SITE EXECUTION</span>
                <span className="metric-number-row">
                  <strong className="metric-number" data-prefix="약 " data-target="70000">약 70,000</strong>
                  <span className="metric-unit">VISITORS</span>
                </span>
                <span className="metric-sub">2025 홍천 행사 관련 방문객</span>
                <span aria-hidden="true" className="metric-track"><span className="metric-fill" data-fill="85" /></span>
              </button>
            </div>
            <p className="meter-explainer">* 게이지 길이는 달성률이나 지표 간 크기 비교를 나타내지 않는 시각적 연출입니다.</p>
            <div className="experience-stack" id="experienceStack">
              <article {...metricPanel(0)} aria-labelledby="metric-tab-0" id="experience-0" role="tabpanel">
                <div className="experience-copy">
                  <div className="micro">SELECTED EXPERIENCE / 01</div>
                  <h3>연결한 바이어가 아니라, 실제로 유통한 경험.</h3>
                  <p>태국 유통채널의 벤더 등록과 제품 납품, 현지 판매 운영까지. 한국 브랜드의 시장 진입을 실질적인 유통 활동으로 연결해 왔습니다.</p>
                  <div className="experience-fact"><strong>42 STORES</strong><span>Robinson 백화점 유통·납품 사례</span></div>
                </div>
                <div className="experience-visual">
                  <img alt="유통·물류 프로젝트 관련 사진" src="/assets/img/pages/about-distribution-containers.jpg" />
                  <span>01 / DISTRIBUTION</span>
                </div>
              </article>
              <article {...metricPanel(1)} aria-labelledby="metric-tab-1" id="experience-1" role="tabpanel">
                <div className="experience-copy">
                  <div className="micro">SELECTED EXPERIENCE / 02</div>
                  <h3>콘텐츠를 만드는 것에서, 실제 판매를 연결하는 것까지.</h3>
                  <p>현지 인플루언서의 콘텐츠 제작과 SNS 홍보, 라이브커머스 운영을 결합하여 브랜드와 태국 소비자의 접점을 넓혀왔습니다.</p>
                  <div className="experience-fact"><strong>31 CREATORS</strong><span>2025 한·태 인플루언서 파일럿 · 라이브 40시간 운영</span></div>
                </div>
                <div className="experience-visual">
                  <img style={{ objectPosition: "50% 40%" }} alt="방콕 스튜디오 라이브커머스 촬영 현장" src="/assets/img/photos/about-exp-digital.jpg" />
                  <span>02 / LIVE STUDIO</span>
                </div>
              </article>
              <article {...metricPanel(2)} aria-labelledby="metric-tab-2" id="experience-2" role="tabpanel">
                <div className="experience-copy">
                  <div className="micro">SELECTED EXPERIENCE / 03</div>
                  <h3>행사를 개최하는 것에서, 현장을 운영하고 결과를 정리하는 것까지.</h3>
                  <p>전시·팝업·바이어 상담 프로그램을 기획하고, 현지 협력사와 참가기업, 운영인력을 조율하며 행사 전 과정을 지원합니다.</p>
                  <div className="experience-fact"><strong>약 7만 명</strong><span>2025 Paradise Park 홍천 행사 관련 방문객 규모</span></div>
                </div>
                <div className="experience-visual">
                  <img style={{ objectPosition: "50% 40%" }} alt="Paradise Park 홍천 특산품 판매전 운영팀" src="/assets/img/photos/about-exp-onsite.jpg" />
                  <span>03 / CASE IMAGE</span>
                </div>
              </article>
            </div>
            <div className="cap-skip"><span>HOVER OR CLICK A METRIC TO EXPLORE</span><span>01 — 03</span></div>
          </div>
        </section>
        <section className="operating" id="about03">
          <div className="wrap">
            <div className="section-head reveal">
              <div>
                <div className="micro">ABOUT-03 / OPERATING FRAMEWORK</div>
                <h2 className="section-h2">두 나라의 역량이 만나,<br />{"하나의 "}<span className="blue">실행체계</span>가 됩니다.</h2>
              </div>
              <div className="right">한국에서는 사업 방향과 기준을 정하고,<br />태국에서는 현지 네트워크와 실행력을 연결합니다.</div>
            </div>
            <div aria-label="한국 인사바른과 태국 INBA의 역할 체계" className="collaboration reveal">
              <article className="role korea">
                <div className="role-text">
                  <div className="role-label">{"KOREA SIDE / STRATEGY & CONTROL"}</div>
                  <h3>{"인사바른 "}<small>INSABAREUN</small></h3>
                  <p className="role-highlight">공공기관 채용·평가 운영과<br />인사·HR 컨설팅 전문</p>
                  <ul><li>공공기관 채용대행 및 전형 운영</li><li>사업기획 및 운영전략</li><li>고객·발주기관 협의</li><li>일정·품질·성과 관리</li><li>행정·정산 및 결과보고 지원</li></ul>
                  <a className="role-link" href="https://www.insabareun.kr/" rel="noopener noreferrer" target="_blank">
                    {"인사바른 홈페이지 방문 "}
                    <span aria-hidden="true">→</span>
                  </a>
                </div>
                <div className="role-mark"><img alt="인사바른 HR Consulting 공식 로고" src="/assets/img/brands/insabareun_logo.png" /></div>
              </article>
              <div aria-label="한국과 태국 역량 결합" className="role-bridge">×</div>
              <article className="role thailand">
                <div className="role-text">
                  <div className="role-label">THAILAND SIDE / LOCAL EXECUTION</div>
                  <h3>{"INBA Thailand "}<small>BANGKOK</small></h3>
                  <p className="role-highlight">태국 시장을 이해하고<br />현지 사업을 실행하는 전문 조직</p>
                  <ul><li>현지 시장·바이어 조사</li><li>수입·인허가·유통 실무</li><li>디지털 마케팅·현장 운영</li><li>현지 협력사·인력 관리</li></ul>
                </div>
                <div className="role-mark inba">
                  <img alt={"INBA International Bridge & Access 공식 로고"} src="/assets/img/brands/inba_logo_circle.png" />
                </div>
              </article>
            </div>
            <div className="flow-note reveal">
              <strong>ONE TEAM, ONE EXECUTION FLOW</strong>
              <span>한국의 기획과 태국의 현장 실행은 아래의 네 단계로 이어집니다.</span>
            </div>
            <div className="sequence reveal" id="workflow">
              <div className="section-head">
                <div>
                  <div className="micro">HOW WE WORK / 01—04</div>
                  <h3 className="section-h2">두 조직의 역할이,<br />하나의 업무 흐름이 됩니다.</h3>
                </div>
                <div className="right">서로 다른 업무를 나열하는 것이 아니라, 하나의 프로젝트를 함께 완성하는 과정입니다. 목표와 역할을 정하고, 현지 관계자를 연결하며, 실행 결과까지 점검합니다.</div>
              </div>
              <div className="process-board" id="processBoard">
                <div aria-label="프로젝트 실행 단계" className="workflow-tabs" role="tablist">
                  {STAGES.map((s, i) => (
                    <Fragment key={s.name}>
                      {i > 0 && " "}
                      <button
                        ref={(el) => {
                          wfTabRefs.current[i] = el;
                        }}
                        aria-controls="workflow-detail"
                        aria-selected={wfTab === i ? "true" : "false"}
                        className={"workflow-tab" + (wfTab === i ? " active" : "")}
                        data-step={i}
                        id={`workflow-tab-${i}`}
                        role="tab"
                        type="button"
                        onMouseEnter={() => {
                          if (window.matchMedia("(hover:hover)").matches) wfIntent.enter(() => workflow(i));
                        }}
                        onMouseLeave={wfIntent.leave}
                        onFocus={() => {
                          wfIntent.leave();
                          workflow(i);
                        }}
                        onClick={() => {
                          wfIntent.leave();
                          workflow(i);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                            e.preventDefault();
                            const n = (i + (e.key === "ArrowRight" ? 1 : STAGES.length - 1)) % STAGES.length;
                            if (wfTabRefs.current[n]) wfTabRefs.current[n].focus();
                          }
                        }}
                      >
                        <small>{s.tab}</small>
                        <strong>{s.name}</strong>
                        <span>{s.ko}</span>
                      </button>
                    </Fragment>
                  ))}
                </div>
                <div
                  aria-labelledby={`workflow-tab-${wfShown}`}
                  aria-live="polite"
                  className={"workflow-detail" + (wfSoft ? " soft-out" : "")}
                  id="workflow-detail"
                  role="tabpanel"
                >
                  <div className="workflow-detail-head">
                    <span className="workflow-number" id="wf-index">{`0${wfShown + 1} / 04`}</span>
                    <h4 id="wf-title">{stage.name}</h4>
                    <b id="wf-ko">{stage.ko}</b>
                  </div>
                  <div className="workflow-detail-text">
                    <p id="wf-desc">{stage.desc}</p>
                    <span id="wf-context">{stage.context}</span>
                  </div>
                </div>
              </div>
              <div className="process-help">PC: 단계에 마우스를 올리거나 클릭해 살펴보기 · 모바일: 터치로 선택</div>
            </div>
            <div className="network">
              <div className="section-head reveal">
                <div>
                  <div className="micro">LOCAL BUSINESS NETWORK / 03 AREAS</div>
                  <h3 className="section-h2">현장에서 실행할 수 있도록,<br />분야별 협력 기반을 연결합니다.</h3>
                </div>
                <div className="right">
                  이 네 단계를 태국 현장에서 실제로 움직이게 하는 기반입니다. 프로젝트의 목적에 따라 유통사·바이어, 현지 크리에이터, 행사 운영 관계자와 협력해 실행에 필요한 접점을 만듭니다.
                </div>
              </div>
              <div className="network-rows">
                <article className="net-card reveal">
                  <div className="net-top">01 / MARKET ACCESS</div>
                  <h4>TRADE</h4>
                  <p><b>유통사·현지 바이어</b><br />제품과 판매채널의 적합성을 검토하고, 상담·유통채널 연결을 지원합니다.</p>
                  <div className="net-application">제품·시장 검토 → 현지 바이어·판매채널 연결</div>
                </article>
                <article className="net-card reveal">
                  <div className="net-top">02 / CUSTOMER CONNECTION</div>
                  <h4>DIGITAL</h4>
                  <p><b>크리에이터·마케팅 협력사</b><br />현지 언어와 소비자 접점에 맞춘 콘텐츠·인플루언서·라이브 협업을 연결합니다.</p>
                  <div className="net-application">현지 콘텐츠 협업 → 소비자 접점·판매 기회 확대</div>
                </article>
                <article className="net-card reveal">
                  <div className="net-top">03 / ON-SITE DELIVERY</div>
                  <h4>EVENTS</h4>
                  <p><b>행사·현장 운영 협력 네트워크</b><br />장소·협력업체·현장 인력과 연결해 프로그램 운영을 지원합니다.</p>
                  <div className="net-application">행사 준비·협력사 연결 → 현장 운영·성과 정리</div>
                </article>
              </div>
              <p className="network-foot">※ 네트워크는 협력 범주를 설명하며, 개별 기업과의 계약·독점 관계를 의미하지 않습니다.</p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
